/**
 * The strings a line's ends store, and the options a line form offers for them.
 *
 * `source_ref` and `dest_ref` are display strings: an equipment tag, `Instrument:
 * <identity>`, `Node #N`, a drain code, an off-sheet reference, or free text. This
 * module is the one place that spells them, and the one place that derives the
 * `*_pnid` sheet a line form prefills for each end.
 */
import { DRAIN_TYPES, LineType, PnidAIData, PnidEquipment, PnidHyperlinkRef, PnidNode, TagSuggestionKind } from '../types';
import { formatInstrumentIdentity, getInstrumentPrimaryLabel } from '../utils/instrumentIdentity';

/** What the line forms know about an instrument on the sheet. */
export interface InstrumentRef {
  id: string;
  instrument_text?: string | null;
  generated_id?: string | null;
  instrument_type?: string | null;
  pnid_hyperlink?: PnidHyperlinkRef | null;
}

export type RefKind = 'equipment' | 'instrument' | 'node' | 'drain' | 'external' | 'manual';
export type LineEnd = 'source' | 'destination';

/** One picked end of a line. `id` is the bound entity; `pnid` is the sheet an off-sheet ref names. */
export interface RefSelection {
  ref: string;
  kind: RefKind;
  id?: string;
  pnid?: string | null;
}

export interface RefOption extends RefSelection {
  label: string;
  detail?: string | null;
}

export type RefGroupName = 'Equipment' | 'Instruments' | 'Lines' | 'Drains' | 'Off-sheet';

export interface RefGroup {
  name: RefGroupName;
  options: RefOption[];
}

export type RefEntity =
  | { kind: 'equipment'; equipment: Pick<PnidEquipment, 'equipment_no'> }
  | { kind: 'instrument'; instrument: InstrumentRef }
  | { kind: 'node'; node: Pick<PnidNode, 'node_no'> };

const INSTRUMENT_REF_PREFIX = 'Instrument: ';

export function refForEntity(entity: RefEntity): string {
  switch (entity.kind) {
    case 'equipment':
      return entity.equipment.equipment_no;
    case 'instrument':
      return `${INSTRUMENT_REF_PREFIX}${formatInstrumentIdentity(entity.instrument)}`;
    case 'node':
      return `Node #${entity.node.node_no}`;
  }
}

/** The node number in text an SME typed as a line reference, or null when the text is not one. */
export function parseNodeRef(text: string): number | null {
  const match = text.trim().match(/^node\s*#?\s*(\d+)$/i);
  return match ? parseInt(match[1], 10) : null;
}

type ExternalEquipmentRef = PnidAIData['external_equipment_refs'][number];

const directionMatchesEnd = (direction: string | null | undefined, end: LineEnd): boolean => {
  const normalized = (direction || '').trim().toLowerCase();
  if (!normalized) return true;
  if (end === 'source') return ['in', 'source', 'from'].includes(normalized);
  return ['out', 'destination', 'dest', 'to'].includes(normalized);
};

const externalOption = (ref: ExternalEquipmentRef, end: LineEnd): RefOption | null => {
  const text = ref.external_ref || (end === 'destination' ? ref.dest_ref : ref.source_ref) || ref.tag;
  if (!text?.trim()) return null;
  const pnid = ref.external_ref
    ? ref.external_pnid || ref.external_drawing || ref.pnid_name || null
    : (end === 'destination' ? ref.dest_pnid : ref.source_pnid) || ref.pnid_name || null;
  const detail = [pnid ? `DWG. ${pnid}` : null, ref.service || ref.reason || ref.context || null]
    .filter(Boolean)
    .join(' · ');
  return { ref: text.trim(), kind: 'external', pnid, label: text.trim(), detail: detail || 'External' };
};

export interface BuildRefOptionsInput {
  equipments: PnidEquipment[];
  instruments: InstrumentRef[];
  nodes: PnidNode[];
  aiData: PnidAIData | null;
  /** The line being edited, left out of Lines so a line cannot name itself. */
  currentNodeId?: string | null;
  end: LineEnd;
}

/** The grouped options for one end of a line. Drains exist for the destination only. */
export function buildRefOptions({ equipments, instruments, nodes, aiData, currentNodeId, end }: BuildRefOptionsInput): RefGroup[] {
  const equipmentOptions: RefOption[] = [];
  const seenTags = new Set<string>();
  for (const equipment of equipments) {
    if (!equipment.equipment_no || seenTags.has(equipment.equipment_no)) continue;
    seenTags.add(equipment.equipment_no);
    const ref = refForEntity({ kind: 'equipment', equipment });
    equipmentOptions.push({
      ref, kind: 'equipment', id: equipment.id, label: ref,
      detail: `${equipment.description} (${equipment.equipment_type})`,
    });
  }
  // Equipment the AI read but nobody has marked yet is still a valid target.
  for (const aiEquipment of aiData?.equipment_in_scope ?? []) {
    if (!aiEquipment.tag || seenTags.has(aiEquipment.tag)) continue;
    seenTags.add(aiEquipment.tag);
    equipmentOptions.push({
      ref: aiEquipment.tag, kind: 'equipment', label: aiEquipment.tag,
      detail: `${aiEquipment.name} (${aiEquipment.type})`,
    });
  }

  const instrumentOptions: RefOption[] = instruments
    .filter((instrument) => instrument.instrument_text || instrument.generated_id)
    .map((instrument) => ({
      ref: refForEntity({ kind: 'instrument', instrument }),
      kind: 'instrument',
      id: instrument.id,
      label: getInstrumentPrimaryLabel(instrument),
      detail: instrument.instrument_type || 'Instrument',
    }));

  const lineOptions: RefOption[] = nodes
    .filter((node) => node.id !== currentNodeId)
    .map((node) => {
      const ref = refForEntity({ kind: 'node', node });
      return { ref, kind: 'node', id: node.id, label: ref, detail: node.line_no || null };
    });

  const drainOptions: RefOption[] = end === 'destination'
    ? Object.entries(DRAIN_TYPES).map(([code, name]) => ({ ref: code, kind: 'drain', label: code, detail: name }))
    : [];

  const offSheetOptions: RefOption[] = [];
  const seenOffSheet = new Set<string>();
  const addOffSheet = (option: RefOption | null) => {
    if (!option) return;
    const key = `${option.ref.toLowerCase()}|${(option.pnid || '').toLowerCase()}`;
    if (seenOffSheet.has(key)) return;
    seenOffSheet.add(key);
    offSheetOptions.push(option);
  };
  for (const ref of aiData?.external_equipment_refs ?? []) {
    if (ref.external_ref && !directionMatchesEnd(ref.direction, end)) continue;
    addOffSheet(externalOption(ref, end));
  }
  for (const tieIn of aiData?.tie_ins ?? []) {
    if (!tieIn.from?.trim()) continue;
    // A tie-in's number is the sheet its end is on: staging stores it as the `*_pnid`.
    addOffSheet({
      ref: tieIn.from.trim(), kind: 'external', pnid: tieIn.tie_in_id?.trim() || null, label: tieIn.from.trim(),
      detail: `Tie-in: ${tieIn.service || tieIn.direction}`,
    });
  }

  const groups: RefGroup[] = [
    { name: 'Equipment', options: equipmentOptions },
    { name: 'Instruments', options: instrumentOptions },
    { name: 'Lines', options: lineOptions },
    { name: 'Drains', options: drainOptions },
    { name: 'Off-sheet', options: offSheetOptions },
  ];
  return groups.filter((group) => group.options.length > 0);
}

/** The tag an equipment or instrument end carries, whose drawings its sheet select offers. */
export interface EndAssetTag {
  kind: TagSuggestionKind;
  tag: string;
}

/** An OPC names the sheet its line continues on, so its end takes that sheet. */
export const isOpc = (instrumentType: string | null | undefined): boolean =>
  (instrumentType || '').trim().toUpperCase() === 'OPC';

/** The tag an end's sheet select offers the drawings of: an equipment's tag, or a non-OPC instrument's text. */
export function endAssetTag(selection: RefSelection, instruments: InstrumentRef[]): EndAssetTag | null {
  if (selection.kind === 'equipment') return { kind: 'equipment', tag: selection.ref };
  if (selection.kind !== 'instrument') return null;
  const instrument = instruments.find((candidate) => candidate.id === selection.id);
  const tag = instrument?.instrument_text?.trim();
  return tag && !isOpc(instrument?.instrument_type) ? { kind: 'instrument', tag } : null;
}

/**
 * The sheet a line form prefills for an end, in `source_pnid` and `dest_pnid`.
 *
 * DECISION(2026-10-23, abhishek): Both ends take one rule — the off-sheet ref's
 * sheet, else the OPC's linked sheet, else the drawing's own `pnid_no` — and the
 * source is not pinned to the current sheet.
 * A line that enters from an OPC has its source on another sheet, and staging
 * records off-sheet sources as often as off-sheet destinations. An export whose
 * "Source PnID" column should always read the current sheet is the signal to
 * apply the off-sheet and OPC rules to the destination only.
 *
 * DECISION(2026-10-26, Abhishek): An empty end and an off-sheet ref with no known sheet prefill the drawing's own sheet.
 * indorama-agent-api `pid_interpreter.py` treats a `*_pnid` that differs from the drawing's as
 * off-sheet, so an end whose sheet nobody knows must not read as off-sheet; the SME edits the field.
 */
export function derivePnid(selection: RefSelection, ownPnidNo: string | null | undefined, instruments: InstrumentRef[]): string {
  if (selection.kind === 'external' && selection.pnid?.trim()) return selection.pnid.trim();
  if (selection.kind === 'instrument' && selection.id) {
    const instrument = instruments.find((candidate) => candidate.id === selection.id);
    const linkedSheet = isOpc(instrument?.instrument_type) ? instrument?.pnid_hyperlink?.pnid_no?.trim() : '';
    if (linkedSheet) return linkedSheet;
  }
  return (ownPnidNo ?? '').trim();
}

/**
 * The option a stored ref names on the sheet, whatever the case, else the text itself as a
 * manual ref. Of off-sheet options that share the text, the first wins.
 */
export function selectionForRef(ref: string, groups: RefGroup[]): RefSelection {
  const needle = ref.trim().toLowerCase();
  const match = groups.flatMap((group) => group.options).find((candidate) => candidate.ref.toLowerCase() === needle);
  return match ?? { ref: ref.trim(), kind: 'manual' };
}

/**
 * The sheet a line form shows, and saves, for an end. `sheetOwner` is the end text the stored
 * sheet was last picked or edited for: while the end reads that text the stored sheet stands,
 * and once the end changes its prefill (`derivePnid`) does.
 */
export function shownEndSheet({ sheetOwner, endText, storedSheet, groups, ownPnidNo, instruments }: {
  sheetOwner: string | null;
  endText: string;
  storedSheet: string;
  groups: RefGroup[];
  ownPnidNo: string | null | undefined;
  instruments: InstrumentRef[];
}): string {
  // DECISION(2026-10-26, Abhishek): A sheet the SME cleared saves blank, with no fallback to the prefill.
  // The SME cleared it on purpose. A blank `*_pnid` that indorama-agent-api `pid_interpreter.py`
  // reads as off-sheet, on a line that is not, is the signal to fall back to the prefill.
  if (sheetOwner === endText) return storedSheet;
  return derivePnid(selectionForRef(endText, groups), ownPnidNo, instruments);
}

/**
 * The selection text typed into a line end and never picked names: its line for a node
 * number, else what `selectionForRef` resolves. Null when the text names a line that is
 * not on the sheet, which the pickers refuse to store.
 */
export function selectionForTypedText(text: string, groups: RefGroup[]): RefSelection | null {
  const nodeNo = parseNodeRef(text);
  if (nodeNo === null) return selectionForRef(text, groups);
  const lineRef = refForEntity({ kind: 'node', node: { node_no: nodeNo } });
  return groups.flatMap((group) => group.options).find((option) => option.kind === 'node' && option.ref === lineRef) ?? null;
}

/**
 * The Line Type a line form shows and saves. `chosen` is the form's own value; `followsEnds`
 * says the line is new and the SME has not touched its Line Type.
 */
export function lineTypeForForm({ chosen, followsEnds, source, destination }: {
  chosen: LineType | undefined;
  followsEnds: boolean;
  source: RefSelection;
  destination: RefSelection;
}): LineType {
  // DECISION(2026-10-27, Abhishek): A new line whose two ends are both instruments defaults to an instrument line, and any other new line to a process line, until the SME sets its Line Type.
  // Two instruments are joined by a signal, not by process flow. A saved line keeps its stored type: the default never rewrites a line an SME or the AI has typed.
  if (!followsEnds) return chosen ?? 'process';
  return source.kind === 'instrument' && destination.kind === 'instrument' ? 'instrument' : 'process';
}
